import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTitle from '../../../components/page-title/PageTitle';
import IPage from '../../../interfaces/IPage';

import { MdOutlineAssignmentReturn } from 'react-icons/md';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import IFormError from '../../../interfaces/IFormError';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Container, Form } from './CompanyForm.styled';
import FieldCoachmark from '../../../components/field-coachmark/FieldCoachmark';
import { sdsGrayscale } from '../../../theme/sds-tokens';
import { COMPANY_FORM_INFO, companyFormInfoId } from './companyFormInfo';
import ICompany from '../../../interfaces/ICompany';
import { useCompanyService } from '../../../hooks/services/useCompanyService';
import { LatenessContainer } from '../../tenant/tenant-form/TenantForm.styled';
import IValidationOption from '../../../interfaces/IValidationOption';
import { useTenantService } from '../../../hooks/services/useTenantService';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface CompanyFormProps extends IPage {}

const initialCompany: ICompany = {
  name: '',
  code: '',
  notificationEmails: '',
  sendNotificationsWhenCreated: false,
  sendNotificationsWhenApproved: false,
  sendNotificationsWhenDisapproved: false,
  sendNotificationsEmployeeWhenArchived: false,
  validationOptionId: 0,
  hoursOfLateness: 0,
  validateLatenessFromBackoffice: false,
  allowJustificationWhenApprovingTheDocument: false,
  sendEmailToManagerWhenDocumentApproved: false,
};

const CompanyForm = (props: CompanyFormProps) => {
  window.document.title = props.title;
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'edit' | 'new'>('edit');
  const [id, setId] = useState(0);
  const [company, setCompany] = useState<ICompany>({ ...initialCompany });
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [actionLabel, setActionLabel] = useState('INCLUSÃO');
  const [validationOptions, setValidationOptions] = React.useState<IValidationOption[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const request = useCompanyService();
  const validationOptionRequest = useTenantService();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;
            
  const isEdit = hasAccessToAction(
    ["parametros-empresa-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const mutedText = (theme: Theme) => (theme.palette.mode === 'dark' ? sdsGrayscale[40] : sdsGrayscale[60]);

  const schema = yup.object({
    name: yup.string().required('Campo obrigatório'),
    notificationEmails: yup
      .string()
      .nullable()
      .email(),
    validationOptionId: yup.number().nullable(),
    hoursOfLateness: yup.number().nullable(),
    validateLatenessFromBackoffice: yup.boolean().nullable(),
    sendNotificationsEmployeeWhenArchived: yup.boolean().nullable(),
    allowJustificationWhenApprovingTheDocument: yup.boolean().nullable(),
    sendEmailToManagerWhenDocumentApproved: yup.boolean().nullable(),
  });

  useEffect(() => {
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';
    const id = parseInt(location.pathname.split('/')[location.pathname.split('/').length - 2]);
    setAction(action);
    if (action === 'edit') {
      setActionLabel('EDIÇÃO');
    } else {
      setActionLabel('INCLUSÃO');
    }
    setId(id);
    if (id !== 0) {
      listCompanyById(id);
    }
  }, []);

  const listCompanyById = async (id: number): Promise<void> => {
    setLoading(true);
    await request
      .findById(id)
      .then(result => {
        setLoading(false);
        setCompany({ ...result });
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const listValidationOptions = async (): Promise<void> => {
    setLoading(true);
    await validationOptionRequest
      .listValidationOptions()
      .then(result => {
        setValidationOptions(result);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const createCompany = async (initialCompany: ITypeDocument): Promise<void> => {
    setLoading(true);
    await request
      .create({ ...company })
      .then(result => {
        setLoading(false);
        setCompany({ ...result });
        toastr.success('Documento cadastrado com sucesso!');
        navigate(`/tenant`);
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const updateCompany = async (id: number, initialCompany: ITypeDocument): Promise<void> => {
    setLoading(true);
    await request
      .update(id, { ...company })
      .then(result => {
        setLoading(false);
        setCompany({ ...result });
        toastr.success('Documento atualizado com sucesso!');
        navigate(`/tenant`);
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const handleCancel = async () => {
    if (action === 'edit') {
      listCompanyById(id);
    } else {
      setCompany({ ...initialCompany });
    }
  };

  const handleSaveClick = async () => {
    const result = await validation.validate(schema, company);
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }

    if (action === 'edit') {
      await updateCompany(id, company);
    } else {
      await createCompany(company);
    }
  };

  useEffect(() => {
    listValidationOptions();
  }, []);

  return (
    <Container>
      <PageTitle title={`${actionLabel} DE EMPRESAS`}>
        <Tooltip title="Voltar para listagem" placement="left-start">
          <IconButton onClick={() => navigate(`/tenant`)} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Código"
              InputLabelProps={{ shrink: true }}
              placeholder="Informe o código da empresa"
              value={company?.code}
              onChange={e => setCompany({ ...company, code: e.target.value })}
              error={Boolean(errors?.code)}
              helperText={errors?.code}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome"
              InputLabelProps={{ shrink: true }}
              placeholder="Informe o nome da empresa"
              value={company?.name}
              onChange={e => setCompany({ ...company, name: e.target.value })}
              error={Boolean(errors?.name)}
              helperText={errors?.name}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                fullWidth
                label="E-mail para notificação"
                InputLabelProps={{ shrink: true }}
                placeholder="Informe notificação"
                value={company?.notificationEmails}
                onChange={e => setCompany({ ...company, notificationEmails: e.target.value })}
                error={Boolean(errors?.notificationEmails)}
                helperText={errors?.notificationEmails}
                inputProps={{ 'aria-describedby': companyFormInfoId('notificationEmails') }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', height: 35, flexShrink: 0 }}>
                <FieldCoachmark
                  descriptionId={companyFormInfoId('notificationEmails')}
                  title={COMPANY_FORM_INFO.notificationEmails.title}
                  description={COMPANY_FORM_INFO.notificationEmails.description}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="helper" sx={{ display: 'block', color: mutedText, marginBottom: '11px' }}>
              Notificações
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Enviar notificações sempre que um atestado for criado"
                    control={
                      <Checkbox
                        onChange={e => setCompany({ ...company, sendNotificationsWhenCreated: e.target.checked })}
                        checked={company.sendNotificationsWhenCreated}
                        inputProps={{ 'aria-describedby': companyFormInfoId('sendNotificationsWhenCreated') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('sendNotificationsWhenCreated')}
                    title={COMPANY_FORM_INFO.sendNotificationsWhenCreated.title}
                    description={COMPANY_FORM_INFO.sendNotificationsWhenCreated.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Enviar notificações sempre que um atestado for aprovado"
                    control={
                      <Checkbox
                        onChange={e => setCompany({ ...company, sendNotificationsWhenApproved: e.target.checked })}
                        checked={company.sendNotificationsWhenApproved}
                        inputProps={{ 'aria-describedby': companyFormInfoId('sendNotificationsWhenApproved') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('sendNotificationsWhenApproved')}
                    title={COMPANY_FORM_INFO.sendNotificationsWhenApproved.title}
                    description={COMPANY_FORM_INFO.sendNotificationsWhenApproved.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Enviar notificações sempre que um atestado for reprovado"
                    control={
                      <Checkbox
                        onChange={e => setCompany({ ...company, sendNotificationsWhenDisapproved: e.target.checked })}
                        checked={company.sendNotificationsWhenDisapproved}
                        inputProps={{ 'aria-describedby': companyFormInfoId('sendNotificationsWhenDisapproved') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('sendNotificationsWhenDisapproved')}
                    title={COMPANY_FORM_INFO.sendNotificationsWhenDisapproved.title}
                    description={COMPANY_FORM_INFO.sendNotificationsWhenDisapproved.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Enviar e-mail ao gestor quando um atestado for aprovado"
                    control={
                      <Checkbox
                        onChange={e =>
                          setCompany({ ...company, sendEmailToManagerWhenDocumentApproved: e.target.checked })
                        }
                        checked={company.sendEmailToManagerWhenDocumentApproved}
                        inputProps={{ 'aria-describedby': companyFormInfoId('sendEmailToManagerWhenDocumentApproved') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('sendEmailToManagerWhenDocumentApproved')}
                    title={COMPANY_FORM_INFO.sendEmailToManagerWhenDocumentApproved.title}
                    description={COMPANY_FORM_INFO.sendEmailToManagerWhenDocumentApproved.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Enviar notificações ao colaborador sempre que um atestado for arquivado"
                    control={
                      <Checkbox
                        onChange={e => setCompany({ ...company, sendNotificationsEmployeeWhenArchived: e.target.checked })}
                        checked={company.sendNotificationsEmployeeWhenArchived}
                        inputProps={{ 'aria-describedby': companyFormInfoId('sendNotificationsEmployeeWhenArchived') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('sendNotificationsEmployeeWhenArchived')}
                    title={COMPANY_FORM_INFO.sendNotificationsEmployeeWhenArchived.title}
                    description={COMPANY_FORM_INFO.sendNotificationsEmployeeWhenArchived.description}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="helper" sx={{ display: 'block', color: mutedText, marginBottom: '11px' }}>
              Regras de validação
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Validar também quando enviado pelo RH"
                    control={
                      <Checkbox
                        onChange={e => setCompany({ ...company, validateLatenessFromBackoffice: e.target.checked })}
                        checked={company.validateLatenessFromBackoffice}
                        inputProps={{ 'aria-describedby': companyFormInfoId('validateLatenessFromBackoffice') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('validateLatenessFromBackoffice')}
                    title={COMPANY_FORM_INFO.validateLatenessFromBackoffice.title}
                    description={COMPANY_FORM_INFO.validateLatenessFromBackoffice.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    label="Permitir justificação quando o documento for aprovado"
                    control={
                      <Checkbox
                        onChange={e =>
                          setCompany({ ...company, allowJustificationWhenApprovingTheDocument: e.target.checked })
                        }
                        checked={company.allowJustificationWhenApprovingTheDocument}
                        inputProps={{ 'aria-describedby': companyFormInfoId('allowJustificationWhenApprovingTheDocument') }}
                      />
                    }
                  />
                  <FieldCoachmark
                    descriptionId={companyFormInfoId('allowJustificationWhenApprovingTheDocument')}
                    title={COMPANY_FORM_INFO.allowJustificationWhenApprovingTheDocument.title}
                    description={COMPANY_FORM_INFO.allowJustificationWhenApprovingTheDocument.description}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    marginLeft: '14px',
                    paddingLeft: '14px',
                    borderLeft: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                <LatenessContainer>
                  <LatenessContainer style={{ minWidth: '20px', marginRight: '10px' }}>
                    <span>Atestados enviados após</span>
                  </LatenessContainer>
                  <LatenessContainer>
                    <TextField
                      inputProps={{ 'aria-describedby': companyFormInfoId('hoursOfLateness') }}
                      value={company?.hoursOfLateness}
                      onChange={e => setCompany({ ...company, hoursOfLateness: parseInt(e.target.value) })}
                      type="number"
                      variant="standard"
                      sx={{ width: '50px', marginRight: '10px' }}
                    />
                    <span style={{ marginRight: '10px' }}>Horas</span>
                  </LatenessContainer>
                  <FormControl variant="standard" sx={{ minWidth: '18px' }}>
                    <Select
                      value={company?.validationOptionId}
                      onChange={e => setCompany({ ...company, validationOptionId: e.target.value as number })}
                    >
                      {validationOptions.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LatenessContainer>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Form>
      <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ marginTop: 5 }} spacing={2}>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
            Cancelar
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSaveClick} disabled={!isEdit}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CompanyForm;
